import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Dropdown générique multi-select avec checkboxes.
 *
 * Pattern Stripe : bouton trigger qui ouvre un popover, options en liste
 * avec checkboxes, fermeture au click outside ou Escape, label dynamique
 * selon la sélection.
 *
 * Différence avec `ColleagueMultiSelect` : générique (pas typé sur
 * TeamMember), accepte `{ value, label, sublabel? }`. Réutilisable pour
 * tout filtre multi-select dans une modale ou une sidebar.
 */
export interface MultiSelectDropdownOption {
  value: string;
  label: string;
  sublabel?: string;
}

export interface MultiSelectDropdownProps {
  options: ReadonlyArray<MultiSelectDropdownOption>;
  selected: string[];
  onChange: (next: string[]) => void;
  /** Label affiché dans le bouton quand aucune sélection. */
  placeholder?: string;
  /** Titre du popover. */
  headerLabel?: string;
  /** Suffixe pluriel (« 3 apporteurs »). */
  pluralLabel?: string;
  className?: string;
}

export function MultiSelectDropdown({
  options,
  selected,
  onChange,
  placeholder = 'Sélectionner',
  headerLabel,
  pluralLabel = 'sélectionnés',
  className,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function toggle(value: string) {
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);
  }

  const triggerLabel =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? `1 ${pluralLabel}`)
        : `${selected.length} ${pluralLabel}`;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex w-full items-center justify-between gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors',
          selected.length > 0
            ? 'border-accent bg-accent/5 text-accent'
            : 'border-border-subtle bg-white text-text-muted hover:text-brand',
        )}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 top-full z-50 mt-1 max-h-72 w-full min-w-[240px] overflow-y-auto rounded-md border border-border-subtle bg-white shadow-popover"
        >
          {(headerLabel || selected.length > 0) && (
            <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
              {headerLabel && (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  {headerLabel}
                </span>
              )}
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="ml-auto inline-flex items-center gap-1 text-[11px] text-text-muted hover:text-accent"
                >
                  <X className="h-3 w-3" />
                  Tout désélectionner
                </button>
              )}
            </div>
          )}
          {options.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-text-muted">
              Aucune option disponible.
            </div>
          ) : (
            <ul>
              {options.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <li key={opt.value}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50',
                        checked && 'bg-accent/5',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(opt.value)}
                        className="accent-accent"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-brand">{opt.label}</div>
                        {opt.sublabel && (
                          <div className="truncate text-[11px] text-text-muted">{opt.sublabel}</div>
                        )}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
